// ABOUTME: Hook for subscription purchase and cancellation actions.
// ABOUTME: Handles product buy and subscription cancel mutations with validation.

import { useCallback, useState } from "react"
import { useQuery, useMutation } from "@apollo/client/react"
import type { ObservableQuery } from "@apollo/client"
import {
  ProductBuyDocument,
  SubscriptionCancelDocument,
  SubscriptionListDocument,
  GetMeDocument,
  BindPaymentMethodDocument,
  SavedPaymentMethodsDocument,
  UserWalletsDocument,
  UserOldDocument,
  PaymentListForBuyerDocument,
  PaymentItemActivationType,
  SubscriptionStatus,
} from "@/graphql/main/gql/graphql"
import type {
  Exact,
  ProductBuyMutation,
  ProductType,
  SubscriptionListFilter,
  SubscriptionListQuery,
  ProductListBuyFragment,
} from "@/graphql/main/gql/graphql"
import { useMeContext } from "@/model/user/provider"
import { useNotificationContext } from "@/app/providers/useNotificationContext"
import { useErrorNotifier } from "@/app/providers/useErrorNotifier"
import { useTranslation } from "react-i18next"

import { validatePaymentData } from "@/features/settings/components/Payment/utils/paymentValidation"
import { useIsMounted } from "@/features/settings/components/Payment/utils/useCleanup"
import { addLogError } from "@/model/logger"
import { safePaymentNavigate } from "@/shared/utils/urlValidation"

type _SubscriptionItemType = SubscriptionListQuery["subscriptionList"]["items"][number]

export const useSubscriptionActions = (
  refetchSubscription: (
    variables?: Partial<Exact<{ filter: SubscriptionListFilter }>> | undefined,
  ) => Promise<ObservableQuery.Result<SubscriptionListQuery>>,
) => {
  const { t } = useTranslation()
  const { me } = useMeContext()
  const { handleNotification } = useNotificationContext()
  const { notifyError } = useErrorNotifier()
  const isMountedRef = useIsMounted()

  const [loadingProductId, setLoadingProductId] = useState<string | null>(null)
  const [cancellingProductTypes, setCancellingProductTypes] = useState<Set<ProductType>>(new Set())

  const { data: dataSubscriptionList } = useQuery(SubscriptionListDocument, {
    variables: { filter: { statuses: [] } },
  })

  const [productBuyMutation] = useMutation(ProductBuyDocument)
  const [subscriptionCancelMutation] = useMutation(SubscriptionCancelDocument)
  const [bindPaymentMethodMutation] = useMutation(BindPaymentMethodDocument, {
    refetchQueries: [{ query: SavedPaymentMethodsDocument }],
  })
  const savedCardsQuery = useQuery(SavedPaymentMethodsDocument)

  /**
   * A trial takes no money now, so a card is bound for when it ends — but only
   * when there is none saved and a provider to bind to. Either way the athlete
   * is told the subscription is active; the binding, when it happens, goes on
   * in the background and reports its own failure.
   */
  const announceTrialActivation = (resolvedProvider: string | undefined) => {
    handleNotification("success", t("common.subscriptionActivated"), true, 3000)

    const hasSavedCards = (savedCardsQuery.data?.savedPaymentMethods.length ?? 0) > 0
    if (hasSavedCards || !resolvedProvider) return

    void bindPaymentMethodMutation({
      variables: {
        input: {
          paymentProviderId: resolvedProvider,
          setAsDefault: true,
        },
      },
    })
      .then(({ data: bindData }) => {
        if (bindData?.bindPaymentMethod.paymentURL) {
          safePaymentNavigate(bindData.bindPaymentMethod.paymentURL)
        }
      })
      .catch((error) => {
        addLogError("Failed to bind card after trial:", error)
      })
  }

  const onSubscribe = useCallback(
    (
      item: ProductListBuyFragment,
      payFromWallet?: boolean,
      paymentProviderID?: string,
      promoCode?: string,
      savedPaymentMethodId?: string,
    ) => {
      // Валидация платежных данных перед обработкой
      const validation = validatePaymentData(item, me.id)

      if (!validation.isValid) {
        // Логируем ошибки валидации
        addLogError("Payment validation failed:", validation.errors)
        handleNotification(
          "error",
          `${t("common.paymentValidationFailed")}: ${validation.errors.map((e) => t(e)).join(", ")}`,
          true,
          5000,
        )
        return
      }

      const { validatedData } = validation
      setLoadingProductId(item.id)

      // Используем переданный providerID или валидированный из продукта
      const resolvedProviderID = payFromWallet ? undefined : paymentProviderID || validatedData!.paymentProviderID

      void productBuyMutation({
        variables: {
          input: {
            activationType: PaymentItemActivationType.Immediate,
            targetUserID: validatedData!.targetUserID,
            amount: validatedData!.amount,
            paymentProviderID: resolvedProviderID,
            productID: validatedData!.productID,
            payFromWallet: payFromWallet ?? false,
            promoCode: promoCode || undefined,
            savedPaymentMethodId: savedPaymentMethodId || undefined,
          },
        },
        refetchQueries: [
          { query: SubscriptionListDocument, variables: { filter: { statuses: [] } } },
          { query: GetMeDocument },
          // The access the purchase grants — me.isPro, the paid-until dates — comes with userOld, not with getMe;
          // variables as useUser watches it (useMe passes the signed-in user's id).
          { query: UserOldDocument, variables: { userID: me.id } },
          { query: UserWalletsDocument },
          // variables must match the watcher in useSubscriptions exactly:
          // Apollo keys cache entries by arguments, so `{}` and `{ paging: null }`
          // are different entries and a mismatched refetch would update neither.
          { query: PaymentListForBuyerDocument, variables: { paging: null } },
        ],
        onCompleted: (data: ProductBuyMutation) => {
          // Безопасно обновляем состояние только если компонент смонтирован
          if (!isMountedRef.current) return

          setLoadingProductId(null)

          // Привязка карты при триале (до return)
          const isTrialActivation = item.trialDays && item.trialDays > 0 && !data.productBuy.url
          if (isTrialActivation) {
            announceTrialActivation(paymentProviderID || validatedData!.paymentProviderID)
            return
          }

          if (data.productBuy.paidFromWallet) {
            handleNotification("success", t("common.paymentSuccesfull"), true, 3000)
            return
          }

          if (savedPaymentMethodId && !data.productBuy.url) {
            handleNotification("success", t("common.paymentProcessed"), true, 5000)
            return
          }

          if (!data.productBuy.url) {
            handleNotification("success", t("common.subscriptionActivated"), true, 3000)
            return
          }

          if (!safePaymentNavigate(data.productBuy.url)) {
            addLogError("Unsafe payment URL blocked:", data.productBuy.url)
            handleNotification("error", t("common.paymentUrlInvalid"), true, 5000)
          }
        },
        onError: (e: Error) => {
          // Безопасно обновляем состояние только если компонент смонтирован
          if (isMountedRef.current) {
            setLoadingProductId(null)
            notifyError(e)
          }
        },
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentional: isMountedRef is a stable ref, not needed in deps
    [productBuyMutation, me.id, handleNotification, notifyError, t, refetchSubscription],
  )

  const onSubscriptionCancel = useCallback(
    async (productType: ProductType) => {
      // const targetSubscriptions = dataSubscriptionList?.subscriptionList.items.filter(
      //   item => item.status === SubscriptionStatus.Active && item.product?.type === productType,
      // )
      // // addLogDebug(targetSubscriptions, "targetSubscriptions")
      // void subscriptionCancelMutation({
      //   variables: { id: targetSubscriptions[0].id, immediately: true },
      // })

      // Проверяем, что компонент смонтирован
      if (!isMountedRef.current) {
        return
      }

      // Предотвращаем дублирование запросов
      if (cancellingProductTypes.has(productType)) {
        return
      }

      const subscriptionItems = dataSubscriptionList?.subscriptionList.items ?? []
      const targetSubscriptions = subscriptionItems.filter(
        (item): item is NonNullable<typeof item> =>
          item.status === SubscriptionStatus.Active && item.product?.type === productType,
      )

      if (targetSubscriptions.length === 0) {
        handleNotification("error", t("common.noActiveSubscriptions"), true, 3000)
        return
      }

      // Отмечаем тип подписки как отменяющийся
      setCancellingProductTypes((prev) => new Set(prev).add(productType))

      // Helper to check mounted status after async operations (function wrapper prevents TypeScript narrowing)
      const checkMounted = () => isMountedRef.current

      try {
        // Batch отмена всех подписок одного типа
        const cancellationPromises = targetSubscriptions.map((subscription) =>
          subscriptionCancelMutation({
            variables: { id: subscription.id, immediately: false },
          }),
        )

        // Ждем завершения всех запросов
        const results = await Promise.allSettled(cancellationPromises)

        // Подсчитываем результаты
        const successful = results.filter(
          (result: PromiseSettledResult<unknown>) => result.status === "fulfilled",
        ).length
        const failed = results.filter((result: PromiseSettledResult<unknown>) => result.status === "rejected").length

        if (successful > 0) {
          // Обновляем данные только один раз после всех отмен
          await refetchSubscription()

          // Проверяем, что компонент все еще смонтирован перед показом уведомлений
          if (checkMounted()) {
            if (failed === 0) {
              handleNotification("success", t("common.allSubscriptionsCancelled"), true, 3000)
            } else {
              handleNotification("error", t("common.someSubscriptionsCancelled", { successful, failed }), true, 5000)
            }
          }
        } else if (checkMounted()) {
          handleNotification("error", t("common.subscriptionCancellationFailed"), true, 3000)
        }

        // Логируем ошибки для отладки
        results.forEach((result: PromiseSettledResult<unknown>, index: number) => {
          if (result.status === "rejected") {
            addLogError(`Subscription cancellation failed for ID ${targetSubscriptions[index]?.id}:`, result.reason)
          }
        })
      } catch (error) {
        addLogError("Unexpected error during subscription cancellation:", error)
        if (checkMounted()) {
          handleNotification("error", t("common.subscriptionCancellationFailed"), true, 3000)
        }
      } finally {
        // Убираем тип подписки из отменяющихся только если компонент смонтирован
        if (checkMounted()) {
          setCancellingProductTypes((prev) => {
            const next = new Set(prev)
            next.delete(productType)
            return next
          })
        }
      }
    },
    [
      dataSubscriptionList,
      subscriptionCancelMutation,
      refetchSubscription,
      handleNotification,
      t,
      cancellingProductTypes,
      isMountedRef,
    ],
  )

  const isLoadingProduct = useCallback(
    (productId: string) => {
      return loadingProductId === productId
    },
    [loadingProductId],
  )

  const isLoadCancelSubscribe = cancellingProductTypes.size > 0

  return {
    onSubscribe,
    onSubscriptionCancel,
    isLoadingProduct,
    isLoadCancelSubscribe,
  }
}
